import { useEffect, useState, type SVGProps, type ReactElement, type ReactNode } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { Button, ImapProviderLogo, ProviderLogo, StatusBadge } from '@immo/ui-kit';
import type {
  AdminMemberOnboardingItem,
  AdminMemberOnboardingItemState,
  GetAdminMemberOnboardingResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import {
  CheckCircleIcon,
  CheckSquareIcon,
  ClockIcon,
  MailIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@/components/layout/icons';
import { Section } from '@/components/detail-layout';

const NS = 'pages.admin.members.detail.onboarding';

type OnboardingState =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly onboarding: GetAdminMemberOnboardingResponse }
  | { readonly status: 'failed' }
  | { readonly status: 'hidden' };

type Translate = (key: string, values?: Record<string, string>) => string;

type RowStyle = {
  readonly icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
  readonly iconClass: string;
  readonly reasonClass: string | null;
};

const DANGER: RowStyle = { icon: XCircleIcon, iconClass: 'text-danger-600', reasonClass: 'text-danger-700' };
const WARNING: RowStyle = { icon: ClockIcon, iconClass: 'text-warning-700', reasonClass: 'text-warning-700' };

const OPTIONAL_MISSING: RowStyle = {
  icon: MinusCircleIcon,
  iconClass: 'text-neutral-400',
  reasonClass: 'text-text-tertiary',
};

const ROW_STYLES: Record<AdminMemberOnboardingItemState, RowStyle> = {
  DONE: { icon: CheckCircleIcon, iconClass: 'text-success-600', reasonClass: null },
  EXPIRING: { icon: CheckCircleIcon, iconClass: 'text-success-600', reasonClass: 'text-warning-700' },
  MISSING: DANGER,
  EXPIRED: { icon: ClockIcon, iconClass: 'text-warning-500', reasonClass: 'text-warning-700' },
  COMPANY_MAILBOX_ONLY: DANGER,
  INVITATION_PENDING: WARNING,
  INVITATION_EXPIRED: DANGER,
  INVITATION_REVOKED: DANGER,
  NOT_APPLICABLE: OPTIONAL_MISSING,
};

export function MemberOnboardingSection({
  memberId,
  revision,
}: {
  readonly memberId: string;
  // Changes identity when the page's data re-loads; a change re-reads without the loading state.
  readonly revision: unknown;
}) {
  const t = useTranslations(NS);
  const [state, setState] = useState<OnboardingState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    apiClient.adminMembers.onboarding({ pathParams: { id: memberId } }).then(
      (result) => {
        if (!active) return;

        if (result.ok) setState({ status: 'loaded', onboarding: result.body });
        else setState({ status: result.status === 403 ? 'hidden' : 'failed' });
      },
      () => {
        if (active) setState({ status: 'failed' });
      },
    );

    return () => {
      active = false;
    };
  }, [memberId, revision, attempt]);

  if (state.status === 'hidden') return null;

  const retry = () => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  };

  const onboarding = state.status === 'loaded' ? state.onboarding : null;
  const subtitle =
    onboarding === null ? undefined : onboarding.complete ? (
      <StatusBadge tone="success" size="sm" label={t('complete')} />
    ) : (
      <span className="whitespace-nowrap">
        {t('progress', { done: String(onboarding.mandatoryDone), total: String(onboarding.mandatoryTotal) })}
      </span>
    );
  const openItems = onboarding?.items.filter((item) => item.mandatory && !item.done) ?? [];
  const lapsingItems = onboarding?.items.filter((item) => item.state === 'EXPIRING') ?? [];
  const collapsedSummary =
    openItems.length + lapsingItems.length === 0 ? undefined : (
      <OnboardingCollapsedSummary open={openItems} lapsing={lapsingItems} />
    );

  return (
    <Section
      collapsible
      title={t('title')}
      icon={CheckSquareIcon}
      subtitle={subtitle}
      collapsedSummary={collapsedSummary}
    >
      {state.status === 'loading' && <p className="text-base-sm text-text-tertiary">{t('loading')}</p>}
      {state.status === 'failed' && (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-md border border-neutral-200 bg-canvas px-4 py-3"
        >
          <p className="text-base-sm text-text-secondary">{t('error')}</p>
          <Button size="sm" variant="secondary" type="button" onClick={retry}>
            {t('retry')}
          </Button>
        </div>
      )}
      {onboarding !== null && (
        <ul className="grid grid-cols-2 grid-flow-col grid-rows-5 gap-x-6 gap-y-2">
          {onboarding.items.map((item) => (
            <OnboardingRow key={item.key} item={item} />
          ))}
        </ul>
      )}
    </Section>
  );
}

const joined = (
  items: ReadonlyArray<AdminMemberOnboardingItem>,
  render: (item: AdminMemberOnboardingItem) => ReactNode,
) => items.flatMap((item, index) => (index === 0 ? [render(item)] : [', ', render(item)]));

function openLabelOf(item: AdminMemberOnboardingItem, t: Translate): string {
  const name = t(`items.${item.key}`);

  switch (item.state) {
    case 'EXPIRED':
      return t('collapsed.expired-suffix', { item: name });
    case 'INVITATION_PENDING':
      return t('collapsed.invitation.pending');
    case 'INVITATION_EXPIRED':
      return t('collapsed.invitation.expired');
    case 'INVITATION_REVOKED':
      return t('collapsed.invitation.revoked');
    default:
      return name;
  }
}

function OnboardingCollapsedSummary({
  open,
  lapsing,
}: {
  readonly open: ReadonlyArray<AdminMemberOnboardingItem>;
  readonly lapsing: ReadonlyArray<AdminMemberOnboardingItem>;
}) {
  const t = useTranslations(NS);
  const intl = useIntl();

  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {open.length > 0 && (
        <span>
          {intl.formatMessage(
            { id: `${NS}.collapsed.open` },
            {
              items: joined(open, (item) => (
                <span key={item.key} className={`whitespace-nowrap ${ROW_STYLES[item.state].reasonClass ?? ''}`}>
                  {openLabelOf(item, t)}
                </span>
              )),
            },
          )}
        </span>
      )}
      {lapsing.length > 0 && (
        <span className="text-warning-700">
          {intl.formatMessage(
            { id: `${NS}.collapsed.lapsing` },
            {
              items: joined(lapsing, (item) => (
                <span key={item.key} className="whitespace-nowrap">
                  {t('collapsed.lapsing-item', { item: t(`items.${item.key}`), ...dateOf(item.at, intl) })}
                </span>
              )),
            },
          )}
        </span>
      )}
    </span>
  );
}

function OnboardingRow({ item }: { readonly item: AdminMemberOnboardingItem }) {
  const t = useTranslations(NS);
  const tRoles = useTranslations('pages.admin.members');
  const intl = useIntl();
  const optionalMissing = item.state === 'MISSING' && !item.mandatory;
  const style = optionalMissing ? OPTIONAL_MISSING : ROW_STYLES[item.state];
  const Icon = style.icon;
  const detail = item.done ? detailOf(item, t, tRoles, intl) : null;
  const showsImapDetail = item.key === 'MAILBOX' && item.provider === 'IMAP' && detail !== null;

  return (
    <li className="flex min-w-0 items-center gap-2 text-base-sm" title={detail ?? undefined}>
      <Icon className={`h-3.5 w-3.5 shrink-0 ${style.iconClass}`} aria-hidden />
      <span className={optionalMissing ? 'shrink-0 text-text-tertiary' : 'shrink-0 text-text-primary'}>
        {t(`items.${item.key}`)}
      </span>
      {style.reasonClass !== null && (
        <span className={`truncate ${style.reasonClass}`}>{` · ${reasonOf(item, optionalMissing, t, intl)}`}</span>
      )}
      {detail !== null && (item.key === 'MAILBOX' || item.key === 'CALENDAR') && (
        <span aria-hidden className="inline-flex min-w-0 items-center gap-1 truncate text-text-tertiary">
          ·
          {showsImapDetail ? (
            item.imapProvider === null ? (
              <MailIcon className="h-3 w-3 shrink-0" aria-hidden />
            ) : (
              <ImapProviderLogo provider={item.imapProvider} size={12} />
            )
          ) : (
            (item.provider === 'GOOGLE' || item.provider === 'MICROSOFT') && (
              <ProviderLogo provider={item.provider} size={12} />
            )
          )}
          <span className="truncate">{detail}</span>
        </span>
      )}
      {detail !== null && <span className="sr-only">{detail}</span>}
    </li>
  );
}

function dateOf(at: string | null, intl: IntlShape): Record<string, string> {
  return at === null ? {} : { date: intl.formatDate(at, { dateStyle: 'medium' }) };
}

function reasonOf(item: AdminMemberOnboardingItem, optionalMissing: boolean, t: Translate, intl: IntlShape): string {
  return t(`states.${optionalMissing ? 'OPTIONAL' : item.state}`, dateOf(item.at, intl));
}

// The German detail text is composed here from the structured evidence, never taken from the backend.
function detailOf(item: AdminMemberOnboardingItem, t: Translate, tRoles: Translate, intl: IntlShape): string | null {
  const providerLabel = item.provider === null ? null : t(`providers.${item.provider}`);

  switch (item.key) {
    case 'FIRST_SIGN_IN':
      return item.at === null ? null : t('details.signed-in-at', dateOf(item.at, intl));
    case 'PROFILE_PHOTO':
      return t('details.photo-present');
    case 'MOBILE':
    case 'POSITION':
      return item.text;
    case 'IMAGE_RIGHTS_CONSENT':
      return item.at === null ? null : t('details.consent-granted-at', dateOf(item.at, intl));
    case 'MAILBOX':
    case 'CALENDAR': {
      if (item.key === 'MAILBOX' && item.provider === 'IMAP') {
        const imapLabel = `IMAP · ${item.imapProvider === null ? (item.imapHost ?? '') : t(`imap-providers.${item.imapProvider}`)}`;

        return item.lastSyncedAt === null
          ? imapLabel
          : t('details.mailbox-synced', {
              provider: imapLabel,
              dateTime: intl.formatDate(item.lastSyncedAt, { dateStyle: 'medium', timeStyle: 'short' }),
            });
      }

      if (providerLabel === null) return null;

      return item.lastSyncedAt === null
        ? providerLabel
        : t('details.mailbox-synced', {
            provider: providerLabel,
            dateTime: intl.formatDate(item.lastSyncedAt, { dateStyle: 'medium', timeStyle: 'short' }),
          });
    }

    case 'WHATSAPP':
      return item.at === null ? null : t('details.whatsapp-since', dateOf(item.at, intl));
    case 'ORGANIZATION_ROLE': {
      const collator = new Intl.Collator(intl.locale);

      return item.organizations
        .map((organization) => {
          const roles = organization.roles.map((slug) => tRoles(`roles.${slug}`)).sort(collator.compare);

          return `${organization.name} · ${roles.join(', ')}`;
        })
        .join(', ');
    }

    case 'RECEIVES_LEADS':
      return item.organizations.map((organization) => organization.name).join(', ');
  }
}
